<template>
  <v-card>
    <v-card-title>{{ title }}</v-card-title>
    <v-card-text>
      <v-skeleton-loader type="paragraph" v-if="!editingCollection" />
      <v-form @submit.prevent v-model="valid" v-else>
        <v-text-field
          :label="$t('name')"
          v-model="editingCollection.name"
          :rules="$rule('required|max128')"
          outlined
          dense
        ></v-text-field>
        <v-text-field
          :label="$t('description')"
          class="mb-3"
          v-model="editingCollection.description"
          :rules="$rule('max255')"
          persistent-hint
          hint="Opcional. Descripción o subtítulo para mostrar al cliente"
          outlined
          dense
        ></v-text-field>
        <div class="mb-2">
          <h4 class="d-inline">Tiendas</h4>
          <span class="pl-2"
            >{{ selectedRestaurants.length }} seleccionadas</span
          >
        </div>
        <div>
          <!--<v-checkbox v-for="role of roles"
                      :key="role.restaurant.id"
                      :label="role.restaurant.name"
                      hide-details
                      dense
                      class="mt-0 col-sm-6 d-inline-block py-1"
                      :input-value="editingCollection.restaurantIds.indexOf(role.restaurant.id) >= 0"
                      @change="toggle(role.restaurant)"
                    />-->

          <v-autocomplete
            :items="notSelectedRestaurants"
            prepend-icon="mdi-magnify"
            clearable
            v-model="searchResult"
            outlined
            dense
            hide-details
            hide-selected
            item-text="name"
            item-value="id"
            label="Nombre del restaurant..."
            return-object
            :search-input.sync="searchInput"
            @change="addRestaurant"
          >
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-title>
                  Ningún restaurant coincide
                </v-list-item-title>
              </v-list-item>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-avatar v-if="item.picture">
                <v-img
                  class="picture"
                  :src="`${uploadsPath}/${item.picture}`"
                ></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title v-text="item.name"></v-list-item-title>
                <!--<v-list-item-subtitle v-text="item.symbol"></v-list-item-subtitle>-->
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <div class="pt-3" v-if="selectedRestaurants.length">
            <v-row
              v-for="r of selectedRestaurants"
              :key="r.id"
              class="align-center justify-space-between"
            >
              <v-col>
                <span>{{ r.name }}</span>
              </v-col>
              <v-spacer />
              <v-col class="py-1">
                <v-select
                  outlined
                  hide-details
                  placeholder="Sucursal"
                  dense
                  item-text="name"
                  item-value="id"
                  :loading="!r.branches"
                  :disabled="r.branches && r.branches.length === 1"
                  :items="r.branches"
                  :value="selectedRestaurantsBranchId[r.id]"
                  @change="chooseBranch(r, $event)"
                />
              </v-col>
              <v-col cols="auto" class="py-0">
                <v-btn icon
                  ><v-icon color="red" @click="removeRestaurant(r)"
                    >mdi-delete</v-icon
                  ></v-btn
                >
              </v-col>
            </v-row>
          </div>
        </div>
      </v-form @submit.prevent>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn text @click="cancel" :disabled="loading">{{ $t('cancel') }}</v-btn>
      <v-btn
        color="primary"
        :disabled="!valid"
        @click="save"
        :loading="loading"
        >{{ $t('save') }}</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
  import { Component, Vue, Prop } from 'vue-property-decorator';
  import ICollection from '../../interfaces/ICollection';
  import CollectionsService from '../../api/services/collections.service';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import RestaurantsService from '../../api/services/restaurants.service';
  import { IMember } from '../../interfaces/IMember';
  import UsersService from '../../api/services/users.service';
  import { Globals } from '../../modules/Globals';
  import { IUser } from '../../interfaces/IUser';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import BranchesService from '../../api/services/branches.service';

  @Component
  export default class NewCollectionModal extends Vue {
    @Prop() user: IUser;
    @Prop() collection: ICollection;
    roles: IMember[] = [];
    restaurants: IRestaurant[] = [];
    usersService = new UsersService();
    branchesService = new BranchesService();
    loading = false;
    collectionsService = new CollectionsService();
    title = '';
    valid = false;
    searchInput: string = '';
    searchResult: IRestaurant = [];
    apiPath = Globals.apiPath;
    uploadsPath = Globals.uploadsPath;
    editingCollection: ICollection = null;
    selectedRestaurants: IRestaurant[] = [];
    selectedRestaurantsBranchId: { [id: number]: number } = {};

    async created() {
      await this.loadRestaurants();

      if (this.collection) {
        const collection = await this.collectionsService.getById(
          this.collection.id
        );
        this.editingCollection = collection;

        const p = [];
        for (const cR of collection.collectionRestaurants) {
          const rest = this.restaurants.find((r) => r.id === cR.restaurantId);
          p.push(this.addRestaurant(rest, cR.branchId));
        }
        await Promise.all(p);
      } else {
        this.editingCollection = <ICollection>{
          name: null,
          description: null,
          collectionRestaurants: [],
        };
      }
    }

    mounted() {
      this.title = !this.collection
        ? 'Nueva colección'
        : `Editando ${this.collection.name}`;
    }

    async loadRestaurants() {
      this.roles = await this.usersService.getRoles(this.user.id);
      this.restaurants = this.roles.map((r) => r.restaurant);
    }

    get notSelectedRestaurants() {
      return this.restaurants.filter(
        (r) => !this.selectedRestaurantsBranchId[r.id]
      );
    }

    async addRestaurant(restaurant: IRestaurant, branchId?: number) {
      this.selectedRestaurants.push(restaurant);
      Vue.set(this.selectedRestaurantsBranchId, restaurant.id, null);
      Vue.nextTick(() => {
        this.searchResult = null;
        this.searchInput = null;
      });
      Vue.set(
        restaurant,
        'branches',
        (await this.branchesService.getAll(restaurant.id)).filter(
          (b) => b.active
        )
      );
      this.chooseBranch(restaurant, branchId ?? restaurant.branches[0].id);
    }

    removeRestaurant(restaurant: IRestaurant) {
      this.selectedRestaurants.splice(
        this.selectedRestaurants.indexOf(restaurant),
        1
      );
      Vue.delete(this.selectedRestaurantsBranchId, restaurant.id);
    }

    chooseBranch(restaurant: IRestaurant, branchId: number) {
      Vue.set(this.selectedRestaurantsBranchId, restaurant.id, branchId);
    }

    async save() {
      this.loading = true;

      const collR = [];
      for (const r of this.selectedRestaurants) {
        collR.push({
          restaurantId: r.id,
          branchId: this.selectedRestaurantsBranchId[r.id],
        });
      }
      this.editingCollection.collectionRestaurants = collR;

      try {
        let collection = !this.collection
          ? await this.collectionsService.create(this.editingCollection)
          : await this.collectionsService.update(this.editingCollection);
        vueData.addAlert('Colección guardada con éxito', AlertType.Success);
        if (!this.collection)
          this.$gtag.event('create', { event_category: 'collection' });
        this.$parent.$emit('saved', collection);
        this.$emit('close');
      } catch (e) {
        console.error(e);
        vueData.serverError(e);
      } finally {
        this.loading = false;
      }
    }

    cancel() {
      this.$emit('close');
    }
  }
</script>
